/**
 * Polish wave 2 — B2 (2026-05-30): §5.12 "Previously selected — use to revert."
 *
 * After a client switch, record the PREVIOUS client on the invoice so the
 * next reopen of the OrgChangePicker can surface it at the top with a
 * "Previously selected — use to revert" caption. This is Christa's
 * footgun-recovery affordance: she switches by mistake, doesn't remember the
 * original was Archivolt at position 8 alphabetical, and currently has no
 * undo signal at the picker level.
 *
 * Storage: sessionStorage, keyed by invoice_id. Window: 1 hour from the
 * switch, or until the tab closes (sessionStorage scope). Per spec §5.12,
 * 1 hour was the lockdown on the lifetime — beyond that the stale prior
 * client is more confusing than useful.
 *
 * Data shape per invoice:
 *   { previousOrgId, previousOrgName, switchedAt (ms epoch) }
 *
 * Functions are intentionally tiny so they can be inlined in tests without
 * mocking. All swallow storage exceptions (e.g. Safari private mode) — the
 * revert affordance is a nice-to-have, not a load-bearing path.
 */

const STORAGE_KEY = 'bg.bill-to.previous-client';
const TTL_MS = 60 * 60 * 1000; // 1 hour per spec §5.12

export interface PreviousClientEntry {
  previousOrgId: string;
  previousOrgName: string;
  switchedAt: number;
}

type Bag = Record<string, PreviousClientEntry>;

function safeReadBag(): Bag {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Bag) : {};
  } catch {
    return {};
  }
}

function safeWriteBag(bag: Bag): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bag));
  } catch {
    // Quota / private mode — silently ignore. The revert hint is non-essential.
  }
}

/**
 * Record the invoice's previous client (the one being switched AWAY from).
 * Call from the success handler of a client-switch PUT.
 *
 * Pass `previousOrgId === null` to clear (e.g. after a revert is consumed
 * so the picker doesn't show a stale prior).
 */
export function recordPreviousClient(
  invoiceId: string,
  previousOrgId: string | null,
  previousOrgName: string | null,
): void {
  const bag = safeReadBag();
  if (!previousOrgId || !previousOrgName) {
    delete bag[invoiceId];
    safeWriteBag(bag);
    return;
  }
  bag[invoiceId] = {
    previousOrgId,
    previousOrgName,
    switchedAt: Date.now(),
  };
  safeWriteBag(bag);
}

/**
 * Read the invoice's previous client, if it's still inside the 1-hour
 * window. Returns null when missing or expired. Expired entries are
 * lazily cleaned on read.
 */
export function getPreviousClient(
  invoiceId: string,
): PreviousClientEntry | null {
  const bag = safeReadBag();
  const entry = bag[invoiceId];
  if (!entry) return null;
  const age = Date.now() - entry.switchedAt;
  if (age > TTL_MS || age < 0) {
    delete bag[invoiceId];
    safeWriteBag(bag);
    return null;
  }
  return entry;
}

/**
 * Clear the invoice's previous-client record. Use after a successful revert
 * (so the next switch's previous client is the one we just landed on, not
 * the original).
 */
export function clearPreviousClient(invoiceId: string): void {
  recordPreviousClient(invoiceId, null, null);
}
